import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'vidat_cookie_consent_v1';

const defaultConsent = {
  necessary: true,
  preferences: false,
  analytics: false,
  thirdParty: false,
  decidedAt: null,
};

const CookieConsentContext = createContext({
  consent: defaultConsent,
  hasDecided: false,
  acceptAll: () => {},
  rejectAll: () => {},
  savePreferences: () => {},
  openSettings: () => {},
  isSettingsOpen: false,
  closeSettings: () => {},
  isBannerOpen: false,
  closeBanner: () => {},
});

const loadConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const persistConsent = (consent) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    /* ignore */
  }
};

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(defaultConsent);
  const [hasDecided, setHasDecided] = useState(false);
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [isBannerOpen, setBannerOpen] = useState(false);

  useEffect(() => {
    const stored = loadConsent();
    if (stored && stored.decidedAt) {
      setConsent({ ...defaultConsent, ...stored });
      setHasDecided(true);
    } else {
      setBannerOpen(true);
    }
  }, []);

  const acceptAll = useCallback(() => {
    const next = {
      necessary: true,
      preferences: true,
      analytics: true,
      thirdParty: true,
      decidedAt: new Date().toISOString(),
    };
    setConsent(next);
    persistConsent(next);
    setHasDecided(true);
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const rejectAll = useCallback(() => {
    const next = {
      necessary: true,
      preferences: false,
      analytics: false,
      thirdParty: false,
      decidedAt: new Date().toISOString(),
    };
    setConsent(next);
    persistConsent(next);
    setHasDecided(true);
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const savePreferences = useCallback((prefs) => {
    const next = {
      necessary: true,
      preferences: !!prefs.preferences,
      analytics: !!prefs.analytics,
      thirdParty: !!prefs.thirdParty,
      decidedAt: new Date().toISOString(),
    };
    setConsent(next);
    persistConsent(next);
    setHasDecided(true);
    setBannerOpen(false);
    setSettingsOpen(false);
  }, []);

  const openSettings = useCallback(() => {
    setSettingsOpen(true);
    setBannerOpen(false);
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
    if (!hasDecided) setBannerOpen(true);
  }, [hasDecided]);

  const closeBanner = useCallback(() => {
    setBannerOpen(false);
  }, []);

  return (
    <CookieConsentContext.Provider value={{
      consent,
      hasDecided,
      acceptAll,
      rejectAll,
      savePreferences,
      openSettings,
      isSettingsOpen,
      closeSettings,
      isBannerOpen,
      closeBanner,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => useContext(CookieConsentContext);
